import React, { useState, useEffect } from 'react'
import { Layout } from 'app/theme/layout'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { decodeHtml } from './helpers'

import { useAppContext } from 'app/AppContext'
import env from 'env'

const POSTS_PER_PAGE = 12

const isDev = env.APP_ENV === 'development'
const isSSR = typeof window === 'undefined'

const protocol = isDev ? 'http://' : 'https://'
const blogUrl = isDev && isSSR ? `${env.INTERNAL_BLOG_URL}/wp-json/wp/v2` : `${protocol + env.INTERNAL_HOST}/wordpress/wp-json/wp/v2`

const fetchBlogPosts = async (page, categorySlug) => {
  try {
    // need to prefetch category if it's in the URL
    const categoryQuery = categorySlug && await fetch(`${blogUrl}/categories?slug=${categorySlug}`)
    const category = categoryQuery && await categoryQuery.json()

    const wpJsonEmbedList = [
      'author',
      'categories',
      'tags',
      'wp:term',
      'wp:featuredmedia'
    ]

    const postFilters = [
      `_embed=${wpJsonEmbedList.join(',')}`,
      `per_page=${POSTS_PER_PAGE}`,
      `page=${page || 1}`
    ]
    if (category?.length) {
      postFilters.push(`categories=${category[0]?.id}`)
    }

    const postsUrl = `${blogUrl}/posts`
    const queryString = postFilters.join('&')
    
    const response = await fetch(`${postsUrl}?${queryString}`)
    const rawBlogData = await response.json()
    const totalPosts = parseInt(response.headers.get('X-WP-Total'))

    return { data: rawBlogData, meta: { totalPosts }, error: null }
  } catch (error) {
    return { data: null, error: error }
  }
}

const fetchBlogCategories = async () => {
  try {
    const categoriesUrl = `${blogUrl}/categories?per_page=100`
    const response = await fetch(categoriesUrl)
    const rawBlogCategories = await response.json()
    return { data: rawBlogCategories, error: null }
  } catch (error) {
    return { data: null, error: error }
  }
}

const BlogPage = () => {
  const params = useParams()
  const context = useAppContext()

  if (isSSR && context.blogCategories === undefined) {
    context.promises = context.promises || []
    context.promises.push(
      fetchBlogCategories().then(data => {
        context.blogCategories = data
      }).catch(() => {
        context.blogCategories = null
      })
    )
  }

  if (isSSR && context.blogPosts === undefined) {
    context.promises = context.promises || []
    context.promises.push(
      fetchBlogPosts(params.page || 1, params.category).then(data => {
        context.blogPosts = data
      }).catch(() => {
        context.blogPosts = null
      })
    )
  }

  const [posts, setPosts] = useState(context.blogPosts?.data)
  const [categories, setCategories] = useState(context.blogCategories?.data)
  const [meta, setMeta] = useState(context.blogPosts?.meta)

  useEffect(() => {
    fetchBlogPosts(params.page || 1, params.category).then(({data, meta, error}) => {
      setPosts(data)
      setMeta(meta)
    })
    if (categories === undefined) {
      fetchBlogCategories().then(({data, error}) => {
        setCategories(data)
      })
    }
  }, [params.page, params.category])


  // setup pagination
  const MAX_PAGES_TO_SHOW = 10
  const totalPosts = parseInt(meta?.totalPosts, 10) 
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE)
  const currentPage = parseInt(params.page, 10) || 1
  const pageArray = []

  // if there are less than MAX_PAGES_TO_SHOW pages, include all of them
  if (totalPages <= MAX_PAGES_TO_SHOW) {
    for (let i = 0; i < totalPages; i++) {
      pageArray.push(i + 1)
    }
  } else {
    // if there are more than MAX_PAGES_TO_SHOW pages, show MAX_PAGES_TO_SHOW pages max
    // the current page should be included in the middle of the array
    const startPage = Math.max(currentPage - Math.floor(MAX_PAGES_TO_SHOW / 2), 0)
    const endPage = Math.min(startPage + MAX_PAGES_TO_SHOW, totalPages - 1)

    for (let i = startPage; i <= endPage; i++) {
      pageArray.push(i + 1)
    }
  }

  const category = categories?.find(c => c.slug === params.category)

  // need to account for the category in the structured data
  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Blog",
    "name": `Mobile Food Alliance Blog${category ? ` - ${category?.name}` : ''}`,
    "description": `MobileFoodAlliance Blog${category ? ` - ${category?.description}` : ''}`,
    "url": `/blog${category ? `/category/${category?.slug}` : ''}`,
  }

  return (
    <Layout className="container py-10">
      <Helmet>
        <title>{`Mobile Food Alliance Blog${category ? ` - ${category?.name}` : ''}`}</title>
        <meta property="og:title" content={`Mobile Food Alliance Blog${category ? ` - ${category?.name}` : ''}`} />
        <meta name="description" content={`Mobile Food Alliance Blog${category ? ` - ${category?.description}` : ''}`} />
        <link rel="canonical" href={`/blog${category ? `/category/${category?.slug}` : ''}`} />
        <link rel="next" href={`/blog${params.category ? `/category/${params.category}` : ''}/page/${currentPage + 1}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url" content={`/blog${params.category ? `/category/${params.category}` : ''}`} />
        <meta property="og:site_name" content="Mobile Food Alliance" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <h1 className="text-5xl font-roboto-flex">Blog</h1>

      <p className="mt-4 mb-2">Categories:</p>
      <div className="flex flex-wrap">
        {categories?.map(category => (
          <a
            key={category.id}
            href={`/blog/category/${category.slug}`}
            className={[
              "text-sm mb-2 font-roboto font-light inline-block text-white rounded-full px-2 mr-1",
              "hover:bg-gray-500",
              params.category === category.slug ? "bg-aegean" : "bg-gray-800"
            ]}
          >
            {category.name}
          </a>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-12 mt-10">
        {posts?.map(post => <BlogPostPreview key={post.slug} post={post} />)}
      </div>
      <div className="mt-10 flex flex-col items-center">
        <div className="flex items-center">
          {currentPage !== 1 && (
            <a
              className="font-roboto mx-2 px-4 py-2 rounded-md bg-aegean text-white"
              href={params.category ? `/blog/category/${params.category}/page/${currentPage - 1}` : `/blog/page/${currentPage - 1}`}
              rel="prev"
              children="Previous"
            />
          )}

          {pageArray.map(page => (
            <a
              key={page}
              href={params.category ? `/blog/category/${params.category}/page/${page}` : `/blog/page/${page}`}
              className={`font-roboto mx-2 px-4 py-2 rounded-md ${currentPage === page ? 'bg-aegean text-white' : 'bg-gray-200'}`}
              children={page}
            />
          ))}
          {currentPage !== totalPages && (
            <a
              href={params.category ? `/blog/category/${params.category}/page/${currentPage + 1}` : `/blog/page/${currentPage + 1}`}
              className="font-roboto mx-2 px-4 py-2 rounded-md bg-aegean text-white"
              children="Next"
              rel="next"
            />
          )}

        </div>
        <p className="mt-4 text-sm text-gray-600">
          Showing {(currentPage - 1) * POSTS_PER_PAGE + 1} - {Math.min(currentPage * POSTS_PER_PAGE, totalPosts)} of {totalPosts} posts
        </p>
      </div>
    </Layout>
  )
}

function BlogPostPreview({post}) {

  const postCategories = post._embedded['wp:term'].filter(arr => arr.length && arr[0]?.taxonomy === 'category')
  const featuredMedia = post._embedded['wp:featuredmedia']?.[0]
  const postImageSrc = featuredMedia?.media_details?.sizes?.medium_large?.source_url
    || featuredMedia?.source_url

  return (
    <div key={post.id} className="flex flex-col">
      <div className="flex-1">
        {featuredMedia && (
          <img
            src={postImageSrc}
            alt={featuredMedia?.alt_text}
            className="w-full object-cover rounded-lg border border-gray-300" 
            height={200} 
            style={{height: '200px'}}
          />
        )}
        <a className="font-roboto-flex text-xl text-aegean font-bold mt-4 inline-block" href={`/blog/${post.slug}`}>
          {decodeHtml(post.title?.rendered)}
        </a>
        <p className="font-light">by {post._embedded?.author[0].name} on {format(new Date(post.date), 'MMM d, yyyy')}</p>
      </div>
      <div className="py-2">
        {postCategories[0]?.map(category =>
          <a
            key={category.slug}
            href={`/blog/category/${category.slug}`}
            className="text-sm font-roboto font-light inline-block bg-gray-800 text-white rounded-full px-2 mr-1"
          >
            {category.name}
          </a>
        )}
      </div>
      <div className="flex flex-col">
        <p className="font-light" dangerouslySetInnerHTML={{ __html: post.excerpt?.rendered}} />
        <a href={`/blog/${post.slug}`} children="Read More" className="font-roboto mt-4 btn btn-snow self-end" />
      </div>
    </div>
  )
}

export default BlogPage
